import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

export const contratApi = axios.create({ baseURL: process.env.REACT_APP_API_URL });

contratApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const chartColors = ['#1f4e5f', '#4f772d', '#c97b2a', '#8d5a97', '#c44536', '#577590', '#9c6644'];
export const levelKeys = ['admin', 'ingenieur', 'pilotage', 'nonAffecte'];
export const levelLabels = {
  admin: 'Admin',
  ingenieur: 'Ingenieur',
  pilotage: 'Pilotage',
  nonAffecte: 'Non affecte',
};
export const levelChartColors = {
  admin: '#1f4e5f',
  ingenieur: '#4f772d',
  pilotage: '#c97b2a',
  nonAffecte: '#c44536',
};
export const frequencyLabel = {
  mensuel: 'Mensuel',
  annuel: 'Annuel',
};

const monthLabels = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const HOURS_PER_DAY = 7;
const SERVICE_METRIC_KEYS = ['admin_hours', 'engineer_hours', 'admin_days', 'engineer_days', 'pilotage_days', 'travel'];
const currencyFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const toProjectId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const getOrganizationRootEntries = (organization = {}) => {
  const roots = toArray(organization?.rootOrganizations);
  if (roots.length > 0) return roots.filter((root) => root?._id);
  if (organization?.rootOrganization?._id) return [organization.rootOrganization];
  return [];
};

const getOrganizationRootIds = (organization = {}) =>
  getOrganizationRootEntries(organization).map((root) => String(root._id));

export const fmtHours = (value) => `${Number(value || 0).toFixed(2)} h`;
export const fmtDays = (value) => `${Number(value || 0).toFixed(2)} j`;
export const formatCurrency = (value) => {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};
export const formatHoursDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(2)} h`;
export const formatDaysDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(2)} j`;
export const formatCountDelta = (value) => `${Number(value) > 0 ? '+' : ''}${Number(value || 0).toFixed(0)}`;
export const normalizeText = (value) => String(value || '').trim().toLowerCase();
export const normalizeActivity = (value) => normalizeText(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

export const isRecupLivraisonEntry = (entry) => {
  const activityName = normalizeActivity(entry?.activity || entry?.activityName || entry?.activityType || '');
  return activityName.includes('recup/livraison materiel')
    || (activityName.includes('recup') && activityName.includes('livraison') && activityName.includes('materiel'));
};
export const formatDateCell = (value) => (value ? new Date(value).toLocaleDateString('fr-FR') : '—');
export const toDays = (hours) => Number(hours || 0) / HOURS_PER_DAY;
export const resolveContractType = (contract = {}) => {
  const rawType = normalizeText(contract?.contractType);
  if (rawType === 'cadre' || rawType === 'infogerance' || rawType === 'basique') return rawType;
  if (contract?.cadreContract === true) return 'cadre';
  if (contract?.infogerance === true) return 'infogerance';
  return '';
};
export const getRawContractType = (contract = {}) => {
  const rawType = normalizeText(contract?.contractType);
  return rawType === 'cadre' || rawType === 'infogerance' || rawType === 'basique' ? rawType : '';
};
export const isCadreContract = (contract = {}) => resolveContractType(contract) === 'cadre';
export const isInfogeranceContract = (contract = {}) => resolveContractType(contract) === 'infogerance';
const contractMatchesTypeFilter = (contract = {}, typeFilter = '') => {
  const contractType = getRawContractType(contract);
  if (typeFilter === '__null__') return !contractType;
  if (!typeFilter) return true;
  return contractType === typeFilter;
};

export const getHoursGap = (stats = {}) =>
  Number(stats?.totalInterventionHours || 0)
  - Number(stats?.helpdeskInterventionHours || 0)
  - Number(stats?.commandeInterventionHours || 0);

const toIsoDateTime = (dateValue, endOfDay = false) => {
  if (!dateValue) return null;
  const suffix = endOfDay ? 'T23:59:59.999' : 'T00:00:00.000';
  return new Date(`${dateValue}${suffix}`).toISOString();
};

export const getConfiguredDays = (contract, dayKey, legacyHourKey) => {
  const configuredDays = Number(contract?.[dayKey] || 0);
  if (configuredDays > 0) return configuredDays;
  return toDays(contract?.[legacyHourKey] || 0);
};

export const getConfiguredDailyRate = (contract, dailyRateKey, legacyHourlyRateKey) => {
  const configuredDailyRate = Number(contract?.[dailyRateKey] || 0);
  if (configuredDailyRate > 0) return configuredDailyRate;
  return Number(contract?.[legacyHourlyRateKey] || 0) * HOURS_PER_DAY;
};

export const getConfiguredHourlyRate = (contract, hourlyRateKey, fallbackDailyRateKey) => {
  const configuredHourlyRate = Number(contract?.[hourlyRateKey] || 0);
  if (configuredHourlyRate > 0) return configuredHourlyRate;
  return Number(contract?.[fallbackDailyRateKey] || 0) / HOURS_PER_DAY;
};

export const normalizeServiceMetric = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return SERVICE_METRIC_KEYS.includes(normalized) ? normalized : '';
};

const normalizeProductCode = (value = '') =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const normalizeCategoryText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const isInfogeranceServiceCategory = (category = '', subCategory = '') => {
  const normalizedCategory = normalizeCategoryText(category);
  const normalizedSubCategory = normalizeCategoryText(subCategory);
  return (normalizedCategory === 'service' || normalizedCategory === 'services')
    && (normalizedSubCategory === 'infogerance' || normalizedSubCategory === 'infogerances');
};

export const detectServiceMetricFromText = (value = '') => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (normalized.includes('heure') && normalized.includes('admin')) return 'admin_hours';
  if (normalized.includes('heure') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_hours';
  if (normalized.includes('jour') && normalized.includes('admin')) return 'admin_days';
  if (normalized.includes('jour') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_days';
  if (normalized.includes('pilotage')) return 'pilotage_days';
  if (normalized.includes('deplacement')) return 'travel';
  return '';
};

export const detectServiceMetricFromCode = (code = '') => {
  const normalized = normalizeProductCode(code);
  if (!normalized) return '';
  if (normalized.includes('TRAVEL') || normalized.includes('DEPLACEMENT')) return 'travel';
  if (normalized.includes('PILOTAGE') && normalized.includes('DAY')) return 'pilotage_days';
  if (normalized.includes('ADMIN') && normalized.includes('HOUR')) return 'admin_hours';
  if (normalized.includes('ENGINEER') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('INGE') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('ADMIN') && normalized.includes('DAY')) return 'admin_days';
  if (normalized.includes('ENGINEER') && normalized.includes('DAY')) return 'engineer_days';
  if (normalized.includes('INGE') && normalized.includes('DAY')) return 'engineer_days';
  return '';
};

export const resolveServiceMetric = ({ serviceMetric, productCode, productName } = {}) => (
  normalizeServiceMetric(serviceMetric)
  || detectServiceMetricFromCode(productCode)
  || detectServiceMetricFromText(productName)
);

const getFallbackServiceRows = (contract) => {
  const adminHourlyRate = getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
  const engineerHourlyRate = getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
  const pilotageDailyRate = getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
  const adminDailyRate = getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
  const engineerDailyRate = getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');

  return [
    { metric: 'admin_hours', cycle: 'mensuel', quantity: Number(contract?.adminHours || 0), unitPrice: adminHourlyRate },
    { metric: 'engineer_hours', cycle: 'mensuel', quantity: Number(contract?.engineerHours || 0), unitPrice: engineerHourlyRate },
    { metric: 'admin_days', cycle: 'annuel', quantity: Number(contract?.adminDays || 0), unitPrice: adminDailyRate },
    { metric: 'engineer_days', cycle: 'annuel', quantity: Number(contract?.engineerDays || 0), unitPrice: engineerDailyRate },
    { metric: 'pilotage_days', cycle: 'annuel', quantity: getConfiguredDays(contract, 'pilotageDays', 'pilotageHours'), unitPrice: pilotageDailyRate },
    { metric: 'travel', cycle: 'annuel', quantity: Number(contract?.travelCount || 0), unitPrice: Number(contract?.travelUnitPrice || 0) },
  ];
};

export const getContractServiceRows = (contract) => {
  const serviceRows = (contract?.articles || [])
    .map((line) => {
      const metric = resolveServiceMetric({
        serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
        productCode: line?.productCode || line?.article?.code,
        productName: line?.productName || line?.article?.name,
      });
      const isService = metric || isInfogeranceServiceCategory(
        line?.category || line?.article?.category || '',
        line?.subCategory || line?.article?.subCategory || ''
      );
      if (!isService) return null;
      return {
        metric: metric || '',
        cycle: String(line?.billingCycle || line?.article?.billingCycle || 'oneshot').toLowerCase(),
        quantity: Number(line?.quantity || 0),
        unitPrice: Number(line?.unitPrice ?? line?.article?.unitPrice ?? 0),
      };
    })
    .filter(Boolean);

  if (serviceRows.length > 0) return serviceRows;
  return getFallbackServiceRows(contract);
};

const getContractMetricQuantityForPeriod = (serviceRows, metric, periodMonthCount) =>
  serviceRows
    .filter((row) => row.metric === metric)
    .reduce((sum, row) => {
      if (row.cycle === 'mensuel') return sum + (row.quantity * periodMonthCount);
      if (row.cycle === 'annuel') return sum + (row.quantity * (periodMonthCount / 12));
      return sum;
    }, 0);

const getContractMetricValueForPeriod = (serviceRows, metric, periodMonthCount) =>
  serviceRows
    .filter((row) => row.metric === metric)
    .reduce((sum, row) => {
      const lineTotal = row.quantity * row.unitPrice;
      if (row.cycle === 'mensuel') return sum + (lineTotal * periodMonthCount);
      if (row.cycle === 'annuel') return sum + (lineTotal * (periodMonthCount / 12));
      return sum;
    }, 0);

const getContractMetricMonthlyQuantity = (serviceRows, metric) =>
  serviceRows
    .filter((row) => row.metric === metric)
    .reduce((sum, row) => {
      if (row.cycle === 'mensuel') return sum + row.quantity;
      if (row.cycle === 'annuel') return sum + (row.quantity / 12);
      return sum;
    }, 0);

const getContractMetricMonthlyValue = (serviceRows, metric) =>
  serviceRows
    .filter((row) => row.metric === metric)
    .reduce((sum, row) => {
      const lineTotal = row.quantity * row.unitPrice;
      if (row.cycle === 'mensuel') return sum + lineTotal;
      if (row.cycle === 'annuel') return sum + (lineTotal / 12);
      return sum;
    }, 0);

export const getMonthRangeKeys = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const keys = [];
  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (cursor <= last) {
    const year = cursor.getUTCFullYear();
    const month = cursor.getUTCMonth();
    keys.push({
      key: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: `${monthLabels[month]} ${year}`,
      year,
      month,
    });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return keys;
};

export const getCustomFieldValue = (entry, names = [], ids = []) => {
  const normalizedNames = names.map(normalizeText);
  const stringIds = ids.map((id) => String(id));
  const field = (entry?.customFields || []).find((item) => {
    if (ids.length > 0 && stringIds.includes(String(item?.id))) {
      return true;
    }
    return normalizedNames.includes(normalizeText(item?.name));
  });

  return typeof field?.value === 'string' ? field.value : field?.value ?? null;
};

export const getEntryTracker = (entry) => {
  const directTracker = entry?.effectiveTracker
    || entry?.ticket?.effectiveTracker
    || entry?.tracker
    || entry?.ticket?.tracker
    || 'Sans tracker';
  const parentTracker = entry?.parentTicket?.tracker
    || entry?.ticket?.parentTicket?.tracker
    || entry?.ticket?.parentTracker;

  return normalizeText(directTracker) === normalizeText('Projet') && parentTracker
    ? parentTracker
    : directTracker;
};

export const getEntryRawTracker = (entry) => (
  entry?.rawTracker
  || entry?.ticket?.rawTracker
  || entry?.ticket?.tracker
  || entry?.tracker
  || 'Sans tracker'
);

export const isInfogeranceEntry = (entry) =>
  normalizeText(getEntryTracker(entry)) === normalizeText('Helpdesk');

export const getFacturableValue = (entry) => {
  const directValue = getCustomFieldValue(entry, ['Facturable ?', 'Facturable?', 'Facturable']);
  const fallbackField = (entry?.customFields || []).find((field) =>
    normalizeText(field?.name).includes('facturable')
  );
  return normalizeText(directValue ?? fallbackField?.value);
};

export const isSurDevisEntry = (entry) => {
  const value = getFacturableValue(entry);
  return value.includes('sur devis') || value.includes('devis');
};

export const getFacturableMode = (entry, contract = null) => {
  const normalizedTracker = normalizeText(getEntryTracker(entry));
  const fallbackMode = normalizedTracker === normalizeText('Helpdesk')
    ? 'infogerance_oui'
    : normalizedTracker === normalizeText('Commande')
      ? 'mode_projet'
      : 'other';
  const value = getFacturableValue(entry);
  if (!value) return fallbackMode;

  if (value.includes('mode projet') || value.includes('modeprojet') || value.includes('projet')) {
    return 'mode_projet';
  }
  if (value.includes('sur devis') || value.includes('devis')) {
    return isCadreContract(contract) && normalizedTracker === normalizeText('Commande')
      ? 'mode_projet'
      : 'other';
  }
  if (
    value === 'oui'
    || value === '1'
    || value === 'true'
    || value.includes('infoger')
    || value.includes('infog')
  ) {
    return 'infogerance_oui';
  }
  if (value === 'non' || value === '0' || value === 'false') return 'other';

  return fallbackMode;
};

export const isModeProjetEntry = (entry, contract = null) =>
  getFacturableMode(entry, contract) === 'mode_projet';

export const resolveEntryMatch = (contract, skill, level) => {
  const matrixCompetences =
    Array.isArray(contract?.competences) && contract.competences.length > 0
      ? contract.competences
      : contract?.baseMatrixId?.competences || [];
  const activeCompetences = matrixCompetences.filter((item) => item?.active !== false);
  const normalizedLevel = normalizeText(level);

  if (!normalizedLevel) {
    return {
      profile: 'nonAffecte',
      matrixMatched: false,
      includedInContract: false,
      matchedLine: null,
    };
  }

  const levelMatch = activeCompetences.find(
    (item) => normalizeText(item.easyRedmineLevel) === normalizedLevel
  );

  if (!levelMatch?.profile) {
    return {
      profile: 'nonAffecte',
      matrixMatched: false,
      includedInContract: false,
      matchedLine: null,
    };
  }

  return {
    profile: levelMatch.profile,
    matrixMatched: true,
    includedInContract: levelMatch.included !== false,
    matchedLine: levelMatch,
  };
};

export function ConsumptionDeltaCell({
  consumed,
  delta,
  contractValue,
  consumedFormatter = fmtHours,
  deltaFormatter = formatHoursDelta,
  tooltipLabel = 'Contrat',
}) {
  const numericDelta = Number(delta || 0);
  const chipStyles = numericDelta < 0
    ? { color: '#b42318', borderColor: '#fda29b', backgroundColor: '#fef3f2' }
    : numericDelta > 0
      ? { color: '#175cd3', borderColor: '#84caff', backgroundColor: '#eff8ff' }
      : { color: '#027a48', borderColor: '#6ce9a6', backgroundColor: '#ecfdf3' };

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2">{consumedFormatter(consumed)}</Typography>
      <Chip size="small" label={deltaFormatter(numericDelta)} variant="outlined" sx={chipStyles} />
      <Tooltip title={`${tooltipLabel}: ${consumedFormatter(contractValue)}`}>
        <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
      </Tooltip>
    </Stack>
  );
}

export function ValueConsumptionCell({ consumedValue, contractValue }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2">{formatCurrency(consumedValue)}</Typography>
      <Tooltip title={`Contrat: ${formatCurrency(contractValue)}`}>
        <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
      </Tooltip>
    </Stack>
  );
}

export function RentaValueCell({ rentaValue, contractValue }) {
  const numericValue = Number(rentaValue || 0);
  const styles = numericValue < 0
    ? { color: '#b42318', backgroundColor: '#fef3f2' }
    : numericValue > 0
      ? { color: '#175cd3', backgroundColor: '#eff8ff' }
      : { color: '#027a48', backgroundColor: '#ecfdf3' };

  return (
    <Tooltip title={`Contrat: ${formatCurrency(contractValue)}`}>
      <Chip size="small" label={formatCurrency(numericValue)} variant="outlined" sx={{ ...styles, borderColor: 'currentColor' }} />
    </Tooltip>
  );
}

function EntryTable({ rows }) {
  const columns = useMemo(
    () => [
      {
        accessorKey: 'spentOn',
        header: 'Date',
        Cell: ({ row }) => (row.original.spentOn ? new Date(row.original.spentOn).toLocaleDateString('fr-FR') : '—'),
      },
      { accessorKey: 'user', header: 'Intervenant' },
      { accessorKey: 'hours', header: 'Heures', Cell: ({ row }) => fmtHours(row.original.hours) },
      { accessorKey: 'competence1', header: 'Competence 1', Cell: ({ row }) => row.original.competence1 || '—' },
      { accessorKey: 'competence2', header: 'Competence 2', Cell: ({ row }) => row.original.competence2 || '—' },
      {
        accessorKey: 'resolvedProfile',
        header: 'Niveau',
        Cell: ({ row }) => {
          const value = row.original.resolvedProfile || 'nonAffecte';
          return (
            <Chip
              size="small"
              label={levelLabels[value] || value}
              color={value === 'ingenieur' ? 'success' : value === 'pilotage' ? 'warning' : 'default'}
            />
          );
        },
      },
      {
        accessorKey: 'includedInContract',
        header: 'Contrat',
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.includedInContract ? 'Dans contrat' : 'Hors contrat'}
            color={row.original.includedInContract ? 'success' : 'warning'}
          />
        ),
      },
      { accessorKey: 'comments', header: 'Commentaire', Cell: ({ row }) => row.original.comments || '—' },
    ],
    []
  );

  const table = useMaterialReactTable({
    enableGlobalFilter: true,
    enableColumnFilters: true,

    localization: MRT_Localization_FR,
    columns,
    data: rows,
    enablePagination: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
 density: 'compact' },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
}

export function ContractTicketsDialog({ open, contract, tickets, entries, onClose, competenceCatalog }) {
  const competence1Id = competenceCatalog?.competence1?.fieldId;
  const competence2Id = competenceCatalog?.competence2?.fieldId;

  const normalizedEntries = useMemo(
    () =>
      (entries || []).map((entry) => {
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);

        return {
          ...entry,
          competence1,
          competence2,
          resolvedProfile: resolvedMatch.profile,
          matrixMatched: resolvedMatch.matrixMatched,
          includedInContract: resolvedMatch.includedInContract,
        };
      }),
    [competence1Id, competence2Id, contract, entries]
  );

  const entriesByTicketId = useMemo(() => {
    const nextMap = new Map();
    normalizedEntries.forEach((entry) => {
      const key = entry.ticketId != null ? String(entry.ticketId) : '__no_ticket__';
      const existing = nextMap.get(key) || [];
      existing.push(entry);
      nextMap.set(key, existing);
    });
    return nextMap;
  }, [normalizedEntries]);

  const ticketRows = useMemo(() => {
    const ticketsById = new Map((tickets || []).map((ticket) => [String(ticket.ticketId), ticket]));

    normalizedEntries.forEach((entry) => {
      const ticketKey = entry.ticketId != null ? String(entry.ticketId) : null;
      if (!ticketKey || ticketsById.has(ticketKey)) return;

      ticketsById.set(ticketKey, {
        ticketId: entry.ticketId,
        subject: entry?.ticket?.subject || entry.ticketSubject || 'Ticket issu du temps saisi',
        tracker: getEntryTracker(entry),
        status: entry?.ticket?.status || '—',
      });
    });

    return Array.from(ticketsById.values())
      .map((ticket) => {
        const ticketEntries = entriesByTicketId.get(String(ticket.ticketId)) || [];
        const matchedCount = ticketEntries.filter((entry) => entry.matrixMatched).length;
        const unmatchedCount = ticketEntries.length - matchedCount;
        const outsideContractCount = ticketEntries.filter(
          (entry) => entry.matrixMatched && !entry.includedInContract
        ).length;

        return {
          ...ticket,
          ticketEntries,
          timeEntryCount: ticketEntries.length,
          totalHours: ticketEntries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0),
          matchedCount,
          unmatchedCount,
          outsideContractCount,
        };
      })
      .sort((a, b) => {
        if (b.timeEntryCount !== a.timeEntryCount) {
          return b.timeEntryCount - a.timeEntryCount;
        }
        return Number(b.ticketId || 0) - Number(a.ticketId || 0);
      });
  }, [entriesByTicketId, normalizedEntries, tickets]);

  const columns = useMemo(
    () => [
      { accessorKey: 'ticketId', header: 'Ticket', Cell: ({ row }) => row.original.ticketId || '—' },
      { accessorKey: 'subject', header: 'Sujet', Cell: ({ row }) => row.original.subject || '—' },
      { accessorKey: 'tracker', header: 'Tracker', Cell: ({ row }) => row.original.tracker || 'Sans tracker' },
      { accessorKey: 'status', header: 'Statut', Cell: ({ row }) => row.original.status || '—' },
      { accessorKey: 'timeEntryCount', header: 'Temps saisis', Cell: ({ row }) => row.original.timeEntryCount || 0 },
      { accessorKey: 'totalHours', header: 'Heures', Cell: ({ row }) => fmtHours(row.original.totalHours) },
      {
        accessorKey: 'matchedCount',
        header: 'Dans matrice',
        Cell: ({ row }) => <Chip size="small" label={row.original.matchedCount || 0} color="success" />,
      },
      {
        accessorKey: 'unmatchedCount',
        header: 'Non trouves',
        Cell: ({ row }) => {
          const value = row.original.unmatchedCount || 0;
          return <Chip size="small" label={value} color={value > 0 ? 'warning' : 'default'} />;
        },
      },
      {
        accessorKey: 'outsideContractCount',
        header: 'Hors contrat',
        Cell: ({ row }) => {
          const value = row.original.outsideContractCount || 0;
          return <Chip size="small" label={value} color={value > 0 ? 'warning' : 'default'} />;
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    enableGlobalFilter: true,
    enableColumnFilters: true,

    localization: MRT_Localization_FR,
    columns,
    data: ticketRows,
    enablePagination: false,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
 density: 'compact' },
    renderDetailPanel: ({ row }) => {
      const matchedEntries = row.original.ticketEntries.filter((entry) => entry.matrixMatched);
      const unmatchedEntries = row.original.ticketEntries.filter((entry) => !entry.matrixMatched);
      const outsideContractEntries = row.original.ticketEntries.filter(
        (entry) => entry.matrixMatched && !entry.includedInContract
      );

      return (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {row.original.ticketEntries.length} temps saisi(s) pour ce ticket.
          </Typography>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Temps trouves dans la matrice</Typography>
            <EntryTable rows={matchedEntries} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Temps effectues hors contrat</Typography>
            <EntryTable rows={outsideContractEntries} />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Temps sans correspondance dans la matrice</Typography>
            <EntryTable rows={unmatchedEntries} />
          </Box>
        </Stack>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </Box>
    ),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{`Tickets - ${contract?.name || 'Contrat'}`}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {(tickets || []).length} ticket(s). Deplie un ticket pour voir les temps saisis et leurs `Compétence 1` / `Compétence 2`.
        </Typography>
        <MaterialReactTable table={table} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

export function ContractFiltersBar({
  availableYears,
  applyDisabled = false,
  clientAutocompleteOptions,
  customEndDate,
  customStartDate,
  dateMode,
  onApply,
  onChangeCustomEndDate,
  onChangeCustomStartDate,
  onChangeDateMode,
  onChangeInfogerance,
  onChangeOrganization,
  onChangeRootOrganization,
  onChangeYear,
  rootOrganizationOptions,
  selectedInfogeranceFilter,
  selectedOrganizationId,
  selectedRootOrganizationId,
  selectedYear,
  showApplyButton = false,
}) {
  const rootOptions = useMemo(
    () => [{ _id: '', name: 'Toutes les racines' }, ...rootOrganizationOptions],
    [rootOrganizationOptions]
  );
  const clientOptions = useMemo(
    () => [{ _id: '', name: 'Tous les clients' }, ...clientAutocompleteOptions],
    [clientAutocompleteOptions]
  );

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: '#cfe0f5',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)',
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          divider={<Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', lg: 'block' }, borderColor: '#d7e3f1' }} />}
        >
          <Box sx={{ flex: { lg: '0 0 340px' }, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  bgcolor: '#dbeafe',
                  color: '#1d4ed8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarMonthOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Temporalité</Typography>
                <Typography variant="caption" color="text.secondary">
                  Choisis la période d’analyse.
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row', lg: 'column' }} spacing={1.25}>
              <TextField
                select
                label="Mode de période"
                value={dateMode}
                onChange={(e) => onChangeDateMode(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="year">Par année</MenuItem>
                <MenuItem value="custom">Personnalisé</MenuItem>
              </TextField>

              {dateMode === 'year' ? (
                <TextField
                  select
                  label="Année"
                  value={selectedYear}
                  onChange={(e) => onChangeYear(e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row', lg: 'column' }} spacing={1.25}>
                  <TextField
                    label="Date début"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => onChangeCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Date fin"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => onChangeCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CorporateFareOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Organisation</Typography>
                <Typography variant="caption" color="text.secondary">
                  Filtre d’abord par racine, puis par client.
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1.25}>
              <Autocomplete
                options={clientOptions}
                value={
                  clientOptions.find(
                    (organization) => String(organization._id) === String(selectedOrganizationId)
                  ) || null
                }
                onChange={(_, value) => onChangeOrganization(value?._id || '')}
                getOptionLabel={(option) => option?.name || 'Sans nom'}
                isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recherche client"
                    placeholder="Tape un nom de client"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchOutlinedIcon fontSize="small" sx={{ color: '#2563eb' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                <Autocomplete
                  options={rootOptions}
                  value={
                    rootOptions.find(
                      (organization) => String(organization._id) === String(selectedRootOrganizationId)
                    ) || null
                  }
                  onChange={(_, value) => onChangeRootOrganization(value?._id || '')}
                  getOptionLabel={(option) => option?.name || 'Sans nom'}
                  isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
                  sx={{ flex: 1, minWidth: 220 }}
                  renderInput={(params) => <TextField {...params} label="Root organization" />}
                />

                <TextField
                  select
                  label="Type de contrat"
                  value={selectedInfogeranceFilter}
                  onChange={(e) => onChangeInfogerance(e.target.value)}
                  sx={{ flex: 1, minWidth: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TuneOutlinedIcon fontSize="small" sx={{ color: '#0f766e' }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="infogerance">Infogérance</MenuItem>
                  <MenuItem value="cadre">Cadre</MenuItem>
                  <MenuItem value="basique">Basique</MenuItem>
                  <MenuItem value="__null__">Non défini</MenuItem>
                </TextField>

                {showApplyButton ? (
                  <Button
                    variant="contained"
                    onClick={onApply}
                    disabled={applyDisabled}
                    sx={{
                      minWidth: { xs: '100%', md: 140 },
                      alignSelf: 'stretch',
                      background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 100%)',
                      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.18)',
                    }}
                  >
                    Valider
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

export function ContractAlerts({ competenceCatalog, mismatchContracts, supportEntriesLoading }) {
  return (
    <>
      {mismatchContracts.length > 0 ? (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            color: '#9a3412',
            backgroundColor: '#fff7ed',
            border: '1px solid #fdba74',
            '& .MuiAlert-icon': {
              color: '#ea580c',
            },
          }}
        >
          {mismatchContracts.length} contrat(s) ont un ecart entre `heures intervention` et `heures InfoG + heures hors InfoG`. Cela indique des trackers autres que `Helpdesk` ou `Commande`: {mismatchContracts.map((contract) => contract.name).join(', ')}
        </Alert>
      ) : null}

      {!competenceCatalog ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Le mapping des competences EasyRedmine n&apos;est pas disponible. La repartition par niveau restera vide ou non affectee tant que `/easyredmine/custom-fields/competences` n&apos;est pas alimente.
        </Alert>
      ) : null}

      {supportEntriesLoading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Chargement des time entries EasyRedmine pour calculer la repartition par niveau.
        </Alert>
      ) : null}
    </>
  );
}

export function useContratPageData({
  autoLoad = true,
  includeMatrices = false,
  includeArticles = false,
  includeSupportProjects = true,
  includeEsetComparison = false,
  includeO365Comparison = false,
  initialSelectedYear,
  requireOrganizationSelection = false,
} = {}) {
  const currentYear = new Date().getFullYear();
  const availableYears = useMemo(
    () => Array.from({ length: 6 }, (_, index) => String(currentYear - index)),
    [currentYear]
  );

  const [contracts, setContracts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [supportProjects, setSupportProjects] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportEntries, setSupportEntries] = useState([]);
  const [supportEntriesLoading, setSupportEntriesLoading] = useState(false);
  const [competenceCatalog, setCompetenceCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialSelectedYear ?? String(currentYear));
  const [dateMode, setDateMode] = useState('year');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedInfogeranceFilter, setSelectedInfogeranceFilter] = useState('');
  const [selectedRootOrganizationId, setSelectedRootOrganizationId] = useState('');
  const [selectedValueProfile, setSelectedValueProfile] = useState('all');
  const [selectedValueMonthKey, setSelectedValueMonthKey] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => (
    autoLoad ? {
      selectedYear: initialSelectedYear ?? String(currentYear),
      dateMode: 'year',
      customStartDate: '',
      customEndDate: '',
      selectedOrganizationId: '',
      selectedInfogeranceFilter: '',
      selectedRootOrganizationId: '',
    } : null
  ));

  const canApplyFilters = useMemo(() => {
    if (requireOrganizationSelection && !selectedOrganizationId) return false;
    if (dateMode === 'year') return Boolean(selectedYear);
    return Boolean(customStartDate && customEndDate);
  }, [
    customEndDate,
    customStartDate,
    dateMode,
    requireOrganizationSelection,
    selectedOrganizationId,
    selectedYear,
  ]);

  const draftFilters = useMemo(() => ({
    selectedYear,
    dateMode,
    customStartDate,
    customEndDate,
    selectedOrganizationId,
    selectedInfogeranceFilter,
    selectedRootOrganizationId,
  }), [
    customEndDate,
    customStartDate,
    dateMode,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
  ]);

  const activeFilters = autoLoad
    ? draftFilters
    : (appliedFilters || {
      selectedYear: '',
      dateMode: 'year',
      customStartDate: '',
      customEndDate: '',
      selectedOrganizationId: '',
      selectedInfogeranceFilter: '',
      selectedRootOrganizationId: '',
    });

  const buildDateRangeFromFilters = useCallback((filters) => {
    if (filters.dateMode === 'custom') {
      return {
        startDate: toIsoDateTime(filters.customStartDate, false),
        endDate: toIsoDateTime(filters.customEndDate, true),
      };
    }

    if (!filters.selectedYear) {
      return { startDate: null, endDate: null };
    }

    return {
      startDate: `${filters.selectedYear}-01-01T00:00:00.000Z`,
      endDate: `${filters.selectedYear}-12-31T23:59:59.999Z`,
    };
  }, []);

  const dateRange = useMemo(() => {
    return buildDateRangeFromFilters(activeFilters);
  }, [activeFilters, buildDateRangeFromFilters]);

  const hasAppliedFilters = autoLoad ? true : Boolean(appliedFilters);
  const contractQueryFilters = autoLoad || hasAppliedFilters ? activeFilters : draftFilters;
  const contractQueryDateRange = useMemo(
    () => buildDateRangeFromFilters(contractQueryFilters),
    [buildDateRangeFromFilters, contractQueryFilters]
  );

  const hasPendingFilterChanges = useMemo(() => {
    if (autoLoad || !appliedFilters) return false;
    return (
      appliedFilters.selectedYear !== selectedYear
      || appliedFilters.dateMode !== dateMode
      || appliedFilters.customStartDate !== customStartDate
      || appliedFilters.customEndDate !== customEndDate
      || appliedFilters.selectedOrganizationId !== selectedOrganizationId
      || appliedFilters.selectedInfogeranceFilter !== selectedInfogeranceFilter
      || appliedFilters.selectedRootOrganizationId !== selectedRootOrganizationId
    );
  }, [
    autoLoad,
    appliedFilters,
    customEndDate,
    customStartDate,
    dateMode,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
  ]);

  const applyFilters = useCallback(() => {
    if (!canApplyFilters) return false;

    setAppliedFilters({
      selectedYear,
      dateMode,
      customStartDate,
      customEndDate,
      selectedOrganizationId,
      selectedInfogeranceFilter,
      selectedRootOrganizationId,
    });
    return true;
  }, [
    canApplyFilters,
    customEndDate,
    customStartDate,
    dateMode,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
  ]);

  const loadContracts = useCallback(async () => {
    if (!contractQueryDateRange.startDate || !contractQueryDateRange.endDate) {
      setContracts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (contractQueryDateRange.startDate) params.startDate = contractQueryDateRange.startDate;
      if (contractQueryDateRange.endDate) params.endDate = contractQueryDateRange.endDate;
      if (contractQueryFilters.selectedInfogeranceFilter) params.contractType = contractQueryFilters.selectedInfogeranceFilter;
      if (includeEsetComparison) params.includeEsetComparison = 'true';
      if (includeO365Comparison) params.includeO365Comparison = 'true';

      const { data } = await contratApi.get('/api/contrat', { params });
      setContracts(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [
    contractQueryDateRange.endDate,
    contractQueryDateRange.startDate,
    contractQueryFilters.selectedInfogeranceFilter,
    includeEsetComparison,
    includeO365Comparison,
  ]);

  const loadOrganizations = useCallback(async () => {
    try {
      const { data } = await contratApi.get('/api/organizations');
      const rows = Array.isArray(data) ? data : data?.data || [];
      setOrganizations(rows);
    } catch (e) {
      setOrganizations([]);
    }
  }, []);

  const loadMatrices = useCallback(async () => {
    if (!includeMatrices) {
      setMatrices([]);
      return;
    }
    try {
      const { data } = await contratApi.get('/api/contrat/matrix');
      setMatrices(Array.isArray(data) ? data : []);
    } catch (e) {
      setMatrices([]);
    }
  }, [includeMatrices]);

  const loadArticles = useCallback(async () => {
    if (!includeArticles) {
      setArticles([]);
      return;
    }
    try {
      const { data } = await contratApi.get('/api/products', {
        params: { page: 1, limit: 500 },
      });
      setArticles(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setArticles([]);
    }
  }, [includeArticles]);

  const loadSupportProjects = useCallback(async () => {
    if (!includeSupportProjects) {
      setSupportProjects([]);
      return;
    }
    try {
      const { data } = await contratApi.get('/api/contrat/support/projects');
      setSupportProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      setSupportProjects([]);
    }
  }, [includeSupportProjects]);

  useEffect(() => {
    loadOrganizations();
    loadMatrices();
    loadArticles();
    loadSupportProjects();
  }, [loadArticles, loadMatrices, loadOrganizations, loadSupportProjects]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    let active = true;

    const loadCompetenceCatalog = async () => {
      try {
        const { data } = await contratApi.get('/api/easyredmine/custom-fields/competences');
        if (active) {
          setCompetenceCatalog(data?.data || null);
        }
      } catch (e) {
        if (active) {
          setCompetenceCatalog(null);
        }
      }
    };

    loadCompetenceCatalog();
    return () => {
      active = false;
    };
  }, []);

  const organizationById = useMemo(() => {
    const nextMap = new Map();
    organizations.forEach((organization) => {
      if (organization?._id) {
        nextMap.set(String(organization._id), organization);
      }
    });
    return nextMap;
  }, [organizations]);

  const organizationsWithContracts = useMemo(() => {
    const nextMap = new Map();
    contracts
      .filter((contract) => contractMatchesTypeFilter(contract, selectedInfogeranceFilter))
      .forEach((contract) => {
        const organizationId = String(contract?.Organization?._id || '');
        if (!organizationId) return;
        const organization = organizationById.get(organizationId) || contract.Organization;
        if (organization?._id) {
          nextMap.set(String(organization._id), organization);
        }
      });

    return Array.from(nextMap.values()).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }, [contracts, organizationById, selectedInfogeranceFilter]);

  const rootOrganizationOptions = useMemo(() => {
    const map = new Map();
    organizationsWithContracts.forEach((organization) => {
      getOrganizationRootEntries(organization).forEach((rootOrganization) => {
        map.set(String(rootOrganization._id), rootOrganization);
      });
    });
    return Array.from(map.values()).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }, [organizationsWithContracts]);

  const clientAutocompleteOptions = useMemo(() => {
    if (!selectedRootOrganizationId) {
      return organizationsWithContracts;
    }

    return organizationsWithContracts.filter((organization) => {
      const rootOrganizationIds = getOrganizationRootIds(organization);
      return rootOrganizationIds.includes(String(selectedRootOrganizationId));
    });
  }, [organizationsWithContracts, selectedRootOrganizationId]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (activeFilters.selectedOrganizationId && contract.Organization?._id !== activeFilters.selectedOrganizationId) {
        return false;
      }
      const contractType = getRawContractType(contract);
      if (activeFilters.selectedInfogeranceFilter === '__null__') {
        if (contractType) return false;
      } else if (activeFilters.selectedInfogeranceFilter && contractType !== activeFilters.selectedInfogeranceFilter) {
        return false;
      }
      if (activeFilters.selectedRootOrganizationId) {
        const organizationDetails = organizationById.get(String(contract.Organization?._id));
        const rootOrganizationIds = getOrganizationRootIds(organizationDetails);
        if (!rootOrganizationIds.includes(String(activeFilters.selectedRootOrganizationId))) {
          return false;
        }
      }
      return true;
    });
  }, [activeFilters, contracts, organizationById]);

  useEffect(() => {
    if (!selectedRootOrganizationId) return;
    const stillAvailable = rootOrganizationOptions.some(
      (organization) => String(organization._id) === String(selectedRootOrganizationId)
    );
    if (!stillAvailable) {
      setSelectedRootOrganizationId('');
    }
  }, [rootOrganizationOptions, selectedRootOrganizationId]);

  useEffect(() => {
    if (!selectedOrganizationId) return;
    const stillAvailable = clientAutocompleteOptions.some(
      (organization) => String(organization._id) === String(selectedOrganizationId)
    );
    if (!stillAvailable) {
      setSelectedOrganizationId('');
    }
  }, [clientAutocompleteOptions, selectedOrganizationId]);

  const supportProjectFamilyById = useMemo(() => {
    const childrenByParentId = new Map();
    (supportProjects || []).forEach((project) => {
      const parentProjectId = toProjectId(project?.parentProjectId);
      const projectId = toProjectId(project?.projectId);
      if (parentProjectId == null || projectId == null) return;
      const existing = childrenByParentId.get(parentProjectId) || [];
      existing.push(projectId);
      childrenByParentId.set(parentProjectId, existing);
    });

    const familyCache = new Map();
    const getFamily = (rootProjectId) => {
      if (rootProjectId == null) return [];
      if (familyCache.has(rootProjectId)) return familyCache.get(rootProjectId);

      const family = [rootProjectId];
      const children = childrenByParentId.get(rootProjectId) || [];
      children.forEach((childProjectId) => {
        family.push(...getFamily(childProjectId));
      });

      const uniqueFamily = Array.from(new Set(family));
      familyCache.set(rootProjectId, uniqueFamily);
      return uniqueFamily;
    };

    const nextMap = new Map();
    (supportProjects || []).forEach((project) => {
      const projectId = toProjectId(project?.projectId);
      if (projectId == null) return;
      nextMap.set(projectId, getFamily(projectId));
    });
    return nextMap;
  }, [supportProjects]);

  const contractProjectIdsById = useMemo(() => {
    const nextMap = new Map();
    filteredContracts.forEach((contract) => {
      const projectIds = (contract.support || []).flatMap((project) => {
        const projectId = toProjectId(project?.projectId);
        if (projectId == null) return [];
        return supportProjectFamilyById.get(projectId) || [projectId];
      });
      nextMap.set(contract._id, Array.from(new Set(projectIds)));
    });
    return nextMap;
  }, [filteredContracts, supportProjectFamilyById]);

  const selectedProjectIds = useMemo(() => {
    const ids = new Set();
    contractProjectIdsById.forEach((projectIds) => {
      (projectIds || []).forEach((projectId) => {
        if (projectId != null) ids.add(projectId);
      });
    });
    return Array.from(ids);
  }, [contractProjectIdsById]);

  useEffect(() => {
    let active = true;

    const loadSupportEntries = async () => {
      if (!hasAppliedFilters || selectedProjectIds.length === 0) {
        if (active) {
          setSupportTickets([]);
          setSupportEntries([]);
          setSupportEntriesLoading(false);
        }
        return;
      }

      setSupportEntriesLoading(true);
      try {
        const params = { limit: 'all', projectIds: selectedProjectIds.join(',') };
        if (dateRange.startDate) params.from = dateRange.startDate;
        if (dateRange.endDate) params.to = dateRange.endDate;

        const [ticketsResponse, entriesResponse] = await Promise.all([
          contratApi.get('/api/easyredmine/tickets', {
            params: {
              limit: 'all',
              projectIds: selectedProjectIds.join(','),
              updatedFrom: dateRange.startDate,
              updatedTo: dateRange.endDate,
            },
          }),
          contratApi.get('/api/easyredmine/time-entries', { params }),
        ]);

        if (active) {
          setSupportTickets(Array.isArray(ticketsResponse?.data?.data) ? ticketsResponse.data.data : []);
          setSupportEntries(Array.isArray(entriesResponse?.data?.data) ? entriesResponse.data.data : []);
        }
      } catch (e) {
        if (active) {
          setSupportTickets([]);
          setSupportEntries([]);
        }
      } finally {
        if (active) {
          setSupportEntriesLoading(false);
        }
      }
    };

    loadSupportEntries();
    return () => {
      active = false;
    };
  }, [dateRange.endDate, dateRange.startDate, hasAppliedFilters, selectedProjectIds]);

  const mismatchContracts = useMemo(
    () =>
      filteredContracts.filter((contract) => {
        const total = Number(contract.supportStats?.totalInterventionHours || 0);
        const infog = Number(contract.supportStats?.helpdeskInterventionHours || 0);
        const commande = Number(contract.supportStats?.commandeInterventionHours || 0);
        return Math.abs(total - (infog + commande)) > 0.0001;
      }),
    [filteredContracts]
  );

  const supportEntriesByProjectId = useMemo(() => {
    const nextMap = new Map();
    supportEntries.forEach((entry) => {
      const key = toProjectId(entry?.projectId);
      if (key == null) return;
      const existing = nextMap.get(key) || [];
      existing.push(entry);
      nextMap.set(key, existing);
    });
    return nextMap;
  }, [supportEntries]);

  const supportLevelStatsByContractId = useMemo(() => {
    const competence1Id = competenceCatalog?.competence1?.fieldId;
    const competence2Id = competenceCatalog?.competence2?.fieldId;
    const nextMap = new Map();

    filteredContracts.forEach((contract) => {
      const contractEntries = (contractProjectIdsById.get(contract._id) || []).flatMap((projectId) =>
        supportEntriesByProjectId.get(projectId) || []
      );
      const totals = {
        totalHours: 0,
        admin: 0,
        ingenieur: 0,
        pilotage: 0,
        nonAffecte: 0,
        trackerBreakdown: new Map(),
      };

      contractEntries.forEach((entry) => {
        if (!isInfogeranceEntry(entry)) return;

        const hours = Number(entry?.hours || 0);
        const tracker = getEntryTracker(entry);
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competence1Id]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competence2Id]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const profile = resolvedMatch.profile;

        totals.totalHours += hours;
        totals[profile] += hours;

        const existingTracker = totals.trackerBreakdown.get(tracker) || {
          tracker,
          totalHours: 0,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };
        existingTracker.totalHours += hours;
        existingTracker[profile] += hours;
        totals.trackerBreakdown.set(tracker, existingTracker);
      });

      nextMap.set(contract._id, {
        totalHours: totals.totalHours,
        admin: totals.admin,
        ingenieur: totals.ingenieur,
        pilotage: totals.pilotage,
        nonAffecte: totals.nonAffecte,
        trackerBreakdown: Array.from(totals.trackerBreakdown.values()).sort((a, b) => b.totalHours - a.totalHours),
      });
    });

    return nextMap;
  }, [competenceCatalog, contractProjectIdsById, filteredContracts, supportEntriesByProjectId]);

  const contractEntriesById = useMemo(() => {
    const nextMap = new Map();
    filteredContracts.forEach((contract) => {
      const contractEntries = (contractProjectIdsById.get(contract._id) || []).flatMap((projectId) =>
        supportEntriesByProjectId.get(projectId) || []
      );
      nextMap.set(contract._id, contractEntries);
    });
    return nextMap;
  }, [contractProjectIdsById, filteredContracts, supportEntriesByProjectId]);

  const supportTicketsByProjectId = useMemo(() => {
    const nextMap = new Map();
    supportTickets.forEach((ticket) => {
      const key = toProjectId(ticket?.projectId);
      if (key == null) return;
      const existing = nextMap.get(key) || [];
      existing.push(ticket);
      nextMap.set(key, existing);
    });
    return nextMap;
  }, [supportTickets]);

  const contractTicketsById = useMemo(() => {
    const nextMap = new Map();
    filteredContracts.forEach((contract) => {
      const seen = new Map();
      (contractProjectIdsById.get(contract._id) || []).forEach((projectId) => {
        (supportTicketsByProjectId.get(projectId) || []).forEach((ticket) => {
          seen.set(String(ticket.ticketId), ticket);
        });
      });
      nextMap.set(contract._id, Array.from(seen.values()));
    });
    return nextMap;
  }, [contractProjectIdsById, filteredContracts, supportTicketsByProjectId]);

  const periodMonths = useMemo(
    () => getMonthRangeKeys(dateRange.startDate, dateRange.endDate),
    [dateRange.endDate, dateRange.startDate]
  );

  const monthlyComparisonRows = useMemo(() => {
    return filteredContracts.flatMap((contract) =>
      periodMonths.map(({ key, label, year, month }) => {
        const serviceRows = getContractServiceRows(contract);
        const contractAdmin = getContractMetricMonthlyQuantity(serviceRows, 'admin_hours');
        const contractIngenieur = getContractMetricMonthlyQuantity(serviceRows, 'engineer_hours');
        const contractPilotage = getContractMetricMonthlyQuantity(serviceRows, 'pilotage_days');
        const contractCadreAdminDays = getContractMetricMonthlyQuantity(serviceRows, 'admin_days');
        const contractCadreIngenieurDays = getContractMetricMonthlyQuantity(serviceRows, 'engineer_days');
        const contractTravel = getContractMetricMonthlyQuantity(serviceRows, 'travel');

        const contractAdminValue = getContractMetricMonthlyValue(serviceRows, 'admin_hours');
        const contractIngenieurValue = getContractMetricMonthlyValue(serviceRows, 'engineer_hours');
        const contractPilotageValue = getContractMetricMonthlyValue(serviceRows, 'pilotage_days');
        const contractCadreAdminValue = getContractMetricMonthlyValue(serviceRows, 'admin_days');
        const contractCadreIngenieurValue = getContractMetricMonthlyValue(serviceRows, 'engineer_days');
        const contractTravelValue = getContractMetricMonthlyValue(serviceRows, 'travel');

        const adminHourlyRate = contractAdmin > 0 ? contractAdminValue / contractAdmin : getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
        const engineerHourlyRate = contractIngenieur > 0 ? contractIngenieurValue / contractIngenieur : getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
        const pilotageDailyRate = contractPilotage > 0 ? contractPilotageValue / contractPilotage : getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
        const adminDailyRate = contractCadreAdminDays > 0 ? contractCadreAdminValue / contractCadreAdminDays : getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
        const engineerDailyRate = contractCadreIngenieurDays > 0 ? contractCadreIngenieurValue / contractCadreIngenieurDays : getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');
        const travelUnitPrice = contractTravel > 0 ? contractTravelValue / contractTravel : Number(contract.travelUnitPrice || 0);
        const row = {
          rowKey: `${contract._id}-${key}`,
          monthKey: key,
          label,
          contractId: contract._id,
          contractName: contract.name || 'Sans nom',
          client: contract.Organization?.name || 'Sans client',
          contractAdmin,
          consumedAdmin: 0,
          contractIngenieur,
          consumedIngenieur: 0,
          contractPilotage,
          consumedPilotage: 0,
          contractCadreAdminDays,
          consumedCadreAdminDays: 0,
          contractCadreIngenieurDays,
          consumedCadreIngenieurDays: 0,
          contractProjectConsultingAdmin: 0,
          consumedProjectConsultingAdmin: 0,
          contractProjectConsultingEngineer: 0,
          consumedProjectConsultingEngineer: 0,
          contractTravel,
          consumedTravel: 0,
          consumedRecupLivraisonHours: 0,
          cadreContract: isCadreContract(contract),
        };

        (contractEntriesById.get(contract._id) || []).forEach((entry) => {
          if (!entry?.spentOn) return;

          const spentOn = new Date(entry.spentOn);
          if (spentOn.getUTCFullYear() !== year || spentOn.getUTCMonth() !== month) return;

          const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competenceCatalog?.competence1?.fieldId]);
          const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competenceCatalog?.competence2?.fieldId]);
          const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
          const hours = Number(entry.hours || 0);
          const days = toDays(hours);
          const facturableMode = getFacturableMode(entry, contract);
          const isRecupLivraison = isRecupLivraisonEntry(entry);

          if (isRecupLivraison) {
            row.consumedRecupLivraisonHours += hours;
            return;
          }

          if (facturableMode === 'infogerance_oui') {
            if (resolvedMatch.profile === 'admin') row.consumedAdmin += hours;
            if (resolvedMatch.profile === 'ingenieur') row.consumedIngenieur += hours;
            if (resolvedMatch.profile === 'pilotage') row.consumedPilotage += days;
          }
          if (facturableMode === 'mode_projet') {
            if (resolvedMatch.profile === 'pilotage') row.consumedPilotage += days;
            if (resolvedMatch.profile === 'admin') row.consumedCadreAdminDays += days;
            if (resolvedMatch.profile === 'ingenieur') row.consumedCadreIngenieurDays += days;
          }

          const hasTravel = (entry.customFields || []).some(
            (field) =>
              normalizeText(field?.name) === normalizeText('Déplacement')
              && ['1', 'true', 'oui'].includes(normalizeText(field?.value))
          );
          if (hasTravel) row.consumedTravel += 1;
        });

        row.deltaAdmin = row.contractAdmin - row.consumedAdmin;
        row.deltaIngenieur = row.contractIngenieur - row.consumedIngenieur;
        row.deltaPilotage = row.contractPilotage - row.consumedPilotage;
        row.deltaCadreAdminDays = row.contractCadreAdminDays - row.consumedCadreAdminDays;
        row.deltaCadreIngenieurDays = row.contractCadreIngenieurDays - row.consumedCadreIngenieurDays;
        row.deltaProjectConsultingAdmin = 0;
        row.deltaProjectConsultingEngineer = 0;
        row.deltaTravel = row.contractTravel - row.consumedTravel;
        row.contractAdminValue =
          row.contractAdmin * adminHourlyRate
          + row.contractCadreAdminDays * adminDailyRate;
        row.consumedAdminValue =
          row.consumedAdmin * adminHourlyRate
          + row.consumedCadreAdminDays * adminDailyRate;
        row.contractIngenieurValue =
          row.contractIngenieur * engineerHourlyRate
          + row.contractCadreIngenieurDays * engineerDailyRate;
        row.consumedIngenieurValue =
          row.consumedIngenieur * engineerHourlyRate
          + row.consumedCadreIngenieurDays * engineerDailyRate;
        row.contractPilotageValue = row.contractPilotage * pilotageDailyRate;
        row.consumedPilotageValue = row.consumedPilotage * pilotageDailyRate;
        row.contractTravelValue = row.contractTravel * travelUnitPrice;
        row.consumedTravelValue = row.consumedTravel * travelUnitPrice;
        row.contractValue =
          row.contractAdminValue
          + row.contractIngenieurValue
          + row.contractPilotageValue
          + row.contractTravelValue;
        row.consumedValue =
          row.consumedAdminValue
          + row.consumedIngenieurValue
          + row.consumedPilotageValue
          + row.consumedTravelValue;
        row.rentaValue = row.contractValue - row.consumedValue;
        row.rentaRate = row.contractValue > 0 ? (row.rentaValue / row.contractValue) * 100 : 0;
        row.isOverrun =
          row.deltaAdmin < 0
          || row.deltaIngenieur < 0
          || row.deltaPilotage < 0
          || row.deltaCadreAdminDays < 0
          || row.deltaCadreIngenieurDays < 0
          || row.deltaTravel < 0;

        return row;
      })
    ).sort((a, b) => {
      if (a.monthKey !== b.monthKey) return a.monthKey.localeCompare(b.monthKey);
      if (a.client !== b.client) return a.client.localeCompare(b.client);
      return a.contractName.localeCompare(b.contractName);
    });
  }, [competenceCatalog, contractEntriesById, filteredContracts, periodMonths]);

  const yearlyComparisonRows = useMemo(() => {
      const periodMonthCount = Math.max(periodMonths.length, 1);

    return filteredContracts.map((contract) => {
      const consumed = {
        admin: 0,
        ingenieur: 0,
        pilotage: 0,
        cadreAdminDays: 0,
        cadreIngenieurDays: 0,
        projectConsultingAdmin: 0,
        projectConsultingEngineer: 0,
        travelCount: 0,
      };
      const serviceRows = getContractServiceRows(contract);
      const contractAdmin = getContractMetricQuantityForPeriod(serviceRows, 'admin_hours', periodMonthCount);
      const contractIngenieur = getContractMetricQuantityForPeriod(serviceRows, 'engineer_hours', periodMonthCount);
      const contractPilotage = getContractMetricQuantityForPeriod(serviceRows, 'pilotage_days', periodMonthCount);
      const contractCadreAdminDays = getContractMetricQuantityForPeriod(serviceRows, 'admin_days', periodMonthCount);
      const contractCadreIngenieurDays = getContractMetricQuantityForPeriod(serviceRows, 'engineer_days', periodMonthCount);
      const contractTravel = getContractMetricQuantityForPeriod(serviceRows, 'travel', periodMonthCount);

      const contractAdminValue = getContractMetricValueForPeriod(serviceRows, 'admin_hours', periodMonthCount);
      const contractIngenieurValue = getContractMetricValueForPeriod(serviceRows, 'engineer_hours', periodMonthCount);
      const contractPilotageValue = getContractMetricValueForPeriod(serviceRows, 'pilotage_days', periodMonthCount);
      const contractCadreAdminValue = getContractMetricValueForPeriod(serviceRows, 'admin_days', periodMonthCount);
      const contractCadreIngenieurValue = getContractMetricValueForPeriod(serviceRows, 'engineer_days', periodMonthCount);
      const contractTravelValue = getContractMetricValueForPeriod(serviceRows, 'travel', periodMonthCount);

      const adminHourlyRate = contractAdmin > 0 ? contractAdminValue / contractAdmin : getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
      const engineerHourlyRate = contractIngenieur > 0 ? contractIngenieurValue / contractIngenieur : getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
      const pilotageDailyRate = contractPilotage > 0 ? contractPilotageValue / contractPilotage : getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
      const adminDailyRate = contractCadreAdminDays > 0 ? contractCadreAdminValue / contractCadreAdminDays : getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
      const engineerDailyRate = contractCadreIngenieurDays > 0 ? contractCadreIngenieurValue / contractCadreIngenieurDays : getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');
      const travelUnitPrice = contractTravel > 0 ? contractTravelValue / contractTravel : Number(contract.travelUnitPrice || 0);
      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        if (!entry) return;

        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competenceCatalog?.competence1?.fieldId]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competenceCatalog?.competence2?.fieldId]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const hours = Number(entry.hours || 0);
        const days = toDays(hours);
        const facturableMode = getFacturableMode(entry, contract);

        if (facturableMode === 'infogerance_oui') {
          if (resolvedMatch.profile === 'admin') consumed.admin += hours;
          if (resolvedMatch.profile === 'ingenieur') consumed.ingenieur += hours;
          if (resolvedMatch.profile === 'pilotage') consumed.pilotage += days;
        }
        if (facturableMode === 'mode_projet') {
          if (resolvedMatch.profile === 'pilotage') consumed.pilotage += days;
          if (resolvedMatch.profile === 'admin') consumed.cadreAdminDays += days;
          if (resolvedMatch.profile === 'ingenieur') consumed.cadreIngenieurDays += days;
        }

        const hasTravel = (entry.customFields || []).some(
          (field) =>
            normalizeText(field?.name) === normalizeText('Déplacement')
            && ['1', 'true', 'oui'].includes(normalizeText(field?.value))
        );
        if (hasTravel) consumed.travelCount += 1;
      });

      const contractProjectConsultingAdmin = 0;
      const contractProjectConsultingEngineer = 0;

      const estimatedContractValue =
        contractAdminValue
        + contractIngenieurValue
        + contractPilotageValue
        + contractCadreAdminValue
        + contractCadreIngenieurValue
        + contractTravelValue;

      const estimatedConsumedValue =
        consumed.admin * adminHourlyRate
        + consumed.ingenieur * engineerHourlyRate
        + consumed.pilotage * pilotageDailyRate
        + consumed.cadreAdminDays * adminDailyRate
        + consumed.cadreIngenieurDays * engineerDailyRate
        + consumed.travelCount * travelUnitPrice;

      return {
        contractId: contract._id,
        contractName: contract.name || 'Sans nom',
        client: contract.Organization?.name || 'Sans client',
        cadreContract: isCadreContract(contract),
        contractAdmin,
        consumedAdmin: consumed.admin,
        deltaAdmin: contractAdmin - consumed.admin,
        contractIngenieur,
        consumedIngenieur: consumed.ingenieur,
        deltaIngenieur: contractIngenieur - consumed.ingenieur,
        contractPilotage,
        consumedPilotage: consumed.pilotage,
        deltaPilotage: contractPilotage - consumed.pilotage,
        contractCadreAdminDays,
        consumedCadreAdminDays: consumed.cadreAdminDays,
        deltaCadreAdminDays: contractCadreAdminDays - consumed.cadreAdminDays,
        contractCadreIngenieurDays,
        consumedCadreIngenieurDays: consumed.cadreIngenieurDays,
        deltaCadreIngenieurDays: contractCadreIngenieurDays - consumed.cadreIngenieurDays,
        contractProjectConsultingAdmin,
        consumedProjectConsultingAdmin: 0,
        deltaProjectConsultingAdmin: 0,
        contractProjectConsultingEngineer,
        consumedProjectConsultingEngineer: 0,
        deltaProjectConsultingEngineer: 0,
        contractTravel,
        consumedTravel: consumed.travelCount,
        deltaTravel: contractTravel - consumed.travelCount,
        contractAdminRate: adminHourlyRate,
        contractIngenieurRate: engineerHourlyRate,
        contractPilotageRate: pilotageDailyRate,
        contractCadreAdminRate: adminDailyRate,
        contractCadreIngenieurRate: engineerDailyRate,
        contractTravelRate: travelUnitPrice,
        contractAdminValue,
        contractIngenieurValue,
        contractPilotageValue,
        contractCadreAdminValue,
        contractCadreIngenieurValue,
        contractTravelValue,
        estimatedContractValue,
        estimatedConsumedValue,
        estimatedDeltaValue: estimatedContractValue - estimatedConsumedValue,
      };
    });
  }, [competenceCatalog, contractEntriesById, filteredContracts, periodMonths.length]);

  const monthlyValueChartData = useMemo(() => {
    const monthMap = new Map();
    monthlyComparisonRows.forEach((row) => {
      const existing = monthMap.get(row.monthKey) || {
        monthKey: row.monthKey,
        label: row.label,
        adminContractValue: 0,
        adminConsumedValue: 0,
        adminRentaValue: 0,
        ingenieurContractValue: 0,
        ingenieurConsumedValue: 0,
        ingenieurRentaValue: 0,
        pilotageContractValue: 0,
        pilotageConsumedValue: 0,
        pilotageRentaValue: 0,
      };

      existing.adminContractValue += Number(row.contractAdminValue || 0);
      existing.adminConsumedValue += Number(row.consumedAdminValue || 0);
      existing.ingenieurContractValue += Number(row.contractIngenieurValue || 0);
      existing.ingenieurConsumedValue += Number(row.consumedIngenieurValue || 0);
      existing.pilotageContractValue += Number(row.contractPilotageValue || 0);
      existing.pilotageConsumedValue += Number(row.consumedPilotageValue || 0);
      existing.adminRentaValue = existing.adminContractValue - existing.adminConsumedValue;
      existing.ingenieurRentaValue = existing.ingenieurContractValue - existing.ingenieurConsumedValue;
      existing.pilotageRentaValue = existing.pilotageContractValue - existing.pilotageConsumedValue;

      monthMap.set(row.monthKey, existing);
    });

    return Array.from(monthMap.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [monthlyComparisonRows]);

  const monthlyValueProfileChartData = useMemo(() => {
    return monthlyValueChartData
      .filter((item) => !selectedValueMonthKey || item.monthKey === selectedValueMonthKey)
      .map((item) => {
        if (selectedValueProfile === 'admin') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.adminContractValue || 0),
            consumedValue: Number(item.adminConsumedValue || 0),
            rentaValue: Number(item.adminRentaValue || 0),
          };
        }

        if (selectedValueProfile === 'pilotage') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.pilotageContractValue || 0),
            consumedValue: Number(item.pilotageConsumedValue || 0),
            rentaValue: Number(item.pilotageRentaValue || 0),
          };
        }

        if (selectedValueProfile === 'ingenieur') {
          return {
            monthKey: item.monthKey,
            label: item.label,
            contractValue: Number(item.ingenieurContractValue || 0),
            consumedValue: Number(item.ingenieurConsumedValue || 0),
            rentaValue: Number(item.ingenieurRentaValue || 0),
          };
        }

        return {
          monthKey: item.monthKey,
          label: item.label,
          adminContractValue: Number(item.adminContractValue || 0),
          adminConsumedValue: Number(item.adminConsumedValue || 0),
          ingenieurContractValue: Number(item.ingenieurContractValue || 0),
          ingenieurConsumedValue: Number(item.ingenieurConsumedValue || 0),
          pilotageContractValue: Number(item.pilotageContractValue || 0),
          pilotageConsumedValue: Number(item.pilotageConsumedValue || 0),
        };
      });
  }, [monthlyValueChartData, selectedValueMonthKey, selectedValueProfile]);

  const trackerChartData = useMemo(() => {
    const trackerMap = new Map();
    filteredContracts.forEach((contract) => {
      (supportLevelStatsByContractId.get(contract._id)?.trackerBreakdown || []).forEach((item) => {
        const key = item.tracker || 'Sans tracker';
        const existing = trackerMap.get(key) || { name: key, value: 0 };
        existing.value += Number(item.totalHours || 0);
        trackerMap.set(key, existing);
      });
    });
    return Array.from(trackerMap.values()).sort((a, b) => b.value - a.value);
  }, [filteredContracts, supportLevelStatsByContractId]);

  const clientHoursChartData = useMemo(
    () =>
      filteredContracts
        .map((contract) => ({
          name: contract.Organization?.name || contract.name || 'Sans client',
          helpdesk: Number(contract.supportStats?.helpdeskInterventionHours || 0),
          commande: Number(contract.supportStats?.commandeInterventionHours || 0),
          other: Number(contract.supportStats?.otherInterventionHours || 0),
        }))
        .sort((a, b) => (b.helpdesk + b.commande + b.other) - (a.helpdesk + a.commande + a.other)),
    [filteredContracts]
  );

  const trackerLevelChartData = useMemo(() => {
    const trackerMap = new Map();
    filteredContracts.forEach((contract) => {
      (supportLevelStatsByContractId.get(contract._id)?.trackerBreakdown || []).forEach((item) => {
        const existing = trackerMap.get(item.tracker) || {
          name: item.tracker,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };
        levelKeys.forEach((levelKey) => {
          existing[levelKey] += Number(item[levelKey] || 0);
        });
        trackerMap.set(item.tracker, existing);
      });
    });
    return Array.from(trackerMap.values()).sort(
      (a, b) => (b.admin + b.ingenieur + b.pilotage + b.nonAffecte) - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
    );
  }, [filteredContracts, supportLevelStatsByContractId]);

  const clientLevelChartData = useMemo(
    () =>
      filteredContracts
        .map((contract) => {
          const levelStats = supportLevelStatsByContractId.get(contract._id) || {};
          return {
            name: contract.Organization?.name || contract.name || 'Sans client',
            admin: Number(levelStats.admin || 0),
            ingenieur: Number(levelStats.ingenieur || 0),
            pilotage: Number(levelStats.pilotage || 0),
            nonAffecte: Number(levelStats.nonAffecte || 0),
          };
        })
        .sort(
          (a, b) => (b.admin + b.ingenieur + b.pilotage + b.nonAffecte) - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
        ),
    [filteredContracts, supportLevelStatsByContractId]
  );

  const userLevelChartData = useMemo(() => {
    const userMap = new Map();
    filteredContracts.forEach((contract) => {
      (contractEntriesById.get(contract._id) || []).forEach((entry) => {
        if (!isInfogeranceEntry(entry)) return;
        const competence1 = getCustomFieldValue(entry, ['Compétence', 'Competence'], [competenceCatalog?.competence1?.fieldId]);
        const competence2 = getCustomFieldValue(entry, ['Compétence2', 'Competence2'], [competenceCatalog?.competence2?.fieldId]);
        const resolvedMatch = resolveEntryMatch(contract, competence1, competence2);
        const userName = entry?.user || 'Sans intervenant';
        const existing = userMap.get(userName) || {
          name: userName,
          admin: 0,
          ingenieur: 0,
          pilotage: 0,
          nonAffecte: 0,
        };
        existing[resolvedMatch.profile] += Number(entry.hours || 0);
        userMap.set(userName, existing);
      });
    });
    return Array.from(userMap.values()).sort(
      (a, b) => (b.admin + b.ingenieur + b.pilotage + b.nonAffecte) - (a.admin + a.ingenieur + a.pilotage + a.nonAffecte)
    );
  }, [competenceCatalog, contractEntriesById, filteredContracts]);

  return {
    api: contratApi,
    articles,
    availableYears,
    clientAutocompleteOptions,
    competenceCatalog,
    contractEntriesById,
    contractTicketsById,
    contracts,
    customEndDate,
    customStartDate,
    dateMode,
    error,
    filteredContracts,
    loadArticles,
    loadContracts,
    loadMatrices,
    loadSupportProjects,
    loading,
    matrices,
    mismatchContracts,
    monthlyComparisonRows,
    monthlyValueChartData,
    monthlyValueProfileChartData,
    organizations,
    periodMonths,
    rootOrganizationOptions,
    applyFilters,
    canApplyFilters,
    hasAppliedFilters,
    hasPendingFilterChanges,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedValueMonthKey,
    selectedValueProfile,
    selectedYear,
    setCustomEndDate,
    setCustomStartDate,
    setSelectedInfogeranceFilter,
    setSelectedOrganizationId,
    setSelectedRootOrganizationId,
    setSelectedValueMonthKey,
    setSelectedValueProfile,
    setSelectedYear,
    setDateMode,
    supportEntriesLoading,
    supportLevelStatsByContractId,
    supportProjects,
    trackerChartData,
    trackerLevelChartData,
    clientHoursChartData,
    clientLevelChartData,
    userLevelChartData,
    yearlyComparisonRows,
  };
}
